import { useEffect, useRef } from "react";
import {
  findAuthorizedLogitechWheel,
  LOGITECH_DEVICE_FILTERS,
} from "../utils/controller/logitech_g923_device";

// 키보드 없이 게이밍 휠(또는 표준 게임패드)로 메뉴/모달을 조작하기 위한 내비게이션 훅.
//
// - direct-control 의 LogitechG923Wheel 드라이버와 독립적으로 동작(권한은 acquire_devices 가
//   이미 획득 — requestDevice 호출 금지, getDevices 로 재사용).
// - 단일 모듈 싱글톤 허브가 장치를 1회만 열고(oninputreport 1개 + 게임패드 폴링 1개),
//   디코드된 "이산 메뉴 이벤트"를 모든 구독자에게 분배한다. → 여러 화면/모달에서 동시에
//   useWheelMenuNav 를 써도 oninputreport 가 서로 덮어쓰지 않는다.
// - 디바운스: 한 번의 물리적 동작 = 한 번의 메뉴 스텝.

export type WheelMenuEvent = "UP" | "DOWN" | "LEFT" | "RIGHT" | "CONFIRM" | "BACK";

// 이벤트 발생원 — pedal(가속/브레이크) / button(버튼·D-pad) / wheel(회전·스틱).
type EventSource = "pedal" | "button" | "wheel";

export interface UseWheelMenuNavOptions {
  enabled?: boolean;
  onEvent: (event: WheelMenuEvent) => void;
  onConnectionChange?: (connected: boolean) => void;
  // 페달로 CONFIRM(가속)/BACK(브레이크)을 받을지. 기본 true.
  // 주행 중 표시되는 모달(제어권 전환 승인 등)에서는 false 로 두어 "버튼으로만" 확정/뒤로를
  // 처리한다 — 가속/브레이크 페달이 실제 주행 입력과 충돌하지 않도록(안전).
  pedalConfirm?: boolean;
  pedalBack?: boolean;
  // 버튼/D-pad 로 CONFIRM/BACK 을 받을지. 기본 true.
  // 모달 승인(버튼)과 모니터링-뒤로(브레이크)가 같은 버튼으로 겹치지 않게 분리할 때 사용.
  buttonConfirm?: boolean;
  buttonBack?: boolean;
}

// ── 임계값 ──
// 휠 회전(약 30°)에서 1 스텝. 데드존 < 임계값 이어야 재무장 정상 동작.
const WHEEL_STEP_THRESHOLD = 40;
const WHEEL_CENTER_DEADZONE = 18;
// 꺾은 채로 유지하면 이 주기로 한 칸씩 자동 반복(요청: 1초마다 한 칸).
const WHEEL_REPEAT_MS = 1000;
const PEDAL_PRESS_THRESHOLD = 0.45;
const GAMEPAD_AXIS_THRESHOLD = 0.6;
const GAMEPAD_AXIS_DEADZONE = 0.25;
const EVENT_MIN_INTERVAL_MS = 180;

const GP_BTN = {
  A: 0,
  B: 1,
  DPAD_UP: 12,
  DPAD_DOWN: 13,
  DPAD_LEFT: 14,
  DPAD_RIGHT: 15,
};

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

interface Subscriber {
  onEventRef: { current: (event: WheelMenuEvent) => void };
  onConnRef: { current?: (connected: boolean) => void };
  enabledRef: { current: boolean };
  pedalConfirmRef: { current: boolean };
  pedalBackRef: { current: boolean };
  buttonConfirmRef: { current: boolean };
  buttonBackRef: { current: boolean };
}

// ───────────────────────── 싱글톤 허브 ─────────────────────────
const subscribers = new Set<Subscriber>();
let started = false;
let disposed = false;
let hidDevice: any = null;
let rafId: number | null = null;
let lastEventAt = 0;
let wheelArmed = true;
// 자동 반복 상태 — 현재 유지 중인 방향(1=다음/-1=이전)과 마지막 이동 시각.
let wheelHoldDir = 0;
let wheelHoldLastStepAt = 0;
let axisArmed = true;
let connected = false;
const prevPressed = {
  accel: false,
  brake: false,
  dpadUp: false,
  dpadDown: false,
  dpadLeft: false,
  dpadRight: false,
  enter: false,
  cross: false,
  circle: false,
};
const gamepadButtonPrev: Record<string, boolean> = {};

// 현재 enabled 이고 브레이크 페달 BACK 을 수신하는 구독자 수 — 진단/로그용.
// 0 이면 디코드는 정상이나 게이트(예: 실제 제어 중 → enabled=false)로 막힌 상태.
function countEnabledBrakeBackSubscribers(): number {
  let count = 0;
  subscribers.forEach((sub) => {
    if (sub.enabledRef.current && sub.pedalBackRef.current) {
      count += 1;
    }
  });
  return count;
}

function dispatch(event: WheelMenuEvent, source: EventSource): void {
  const now = Date.now();
  if (now - lastEventAt < EVENT_MIN_INTERVAL_MS) {
    return;
  }
  lastEventAt = now;
  subscribers.forEach((sub) => {
    if (!sub.enabledRef.current) return;
    if (event === "CONFIRM" && source === "pedal" && !sub.pedalConfirmRef.current) {
      return;
    }
    if (event === "BACK" && source === "pedal" && !sub.pedalBackRef.current) {
      return;
    }
    if (event === "CONFIRM" && source === "button" && !sub.buttonConfirmRef.current) {
      return;
    }
    if (event === "BACK" && source === "button" && !sub.buttonBackRef.current) {
      return;
    }
    sub.onEventRef.current(event);
  });
}

function setConnected(next: boolean): void {
  if (connected === next) return;
  connected = next;
  // 항상 켜진 진단 — 허브가 연결됨/끊김을 보고하는 시점. 콕핏에서 휠 BACK 이
  // 안 먹으면 가장 먼저 connected:true 가 찍히는지 확인한다.
  console.log("[WHEEL] connection change", {
    connected: next,
    hidDeviceOpen: hidDevice?.opened ?? null,
    totalSubscribers: subscribers.size,
  });
  subscribers.forEach((sub) => sub.onConnRef.current?.(next));
}

function decodeAndEmit(data: DataView): void {
  const payload = new Uint8Array(data.buffer);

  // 스티어링: 바이트 42(lsb), 43(msb).
  const steeringRaw = (payload[43] << 8) | payload[42];
  const steering = -(0.0148 * steeringRaw - 485);

  // 우회전 = RIGHT/DOWN(다음), 좌회전 = LEFT/UP(이전). (이 하드웨어는 우회전 시 음수.)
  // 한 번 꺾으면 한 칸 이동하고, 꺾은 채로 유지하면 WHEEL_REPEAT_MS 마다 한 칸씩
  // 자동 반복한다(요청). 중앙으로 돌아오면 다시 즉시 한 칸 이동할 수 있게 재무장한다.
  const wheelDir =
    steering <= -WHEEL_STEP_THRESHOLD
      ? 1 // 우회전 → 다음(RIGHT/DOWN)
      : steering >= WHEEL_STEP_THRESHOLD
        ? -1 // 좌회전 → 이전(LEFT/UP)
        : 0;

  if (wheelDir !== 0) {
    const now = Date.now();
    const isFirstStep = wheelArmed || wheelDir !== wheelHoldDir;
    if (isFirstStep || now - wheelHoldLastStepAt >= WHEEL_REPEAT_MS) {
      if (wheelDir === 1) {
        dispatch("RIGHT", "wheel");
        dispatch("DOWN", "wheel");
      } else {
        dispatch("LEFT", "wheel");
        dispatch("UP", "wheel");
      }
      wheelArmed = false;
      wheelHoldDir = wheelDir;
      wheelHoldLastStepAt = now;
    }
  } else if (Math.abs(steering) <= WHEEL_CENTER_DEADZONE) {
    wheelArmed = true;
    wheelHoldDir = 0;
  }

  // 가속 페달 → CONFIRM(source=pedal).
  const accelRaw = (payload[45] << 8) | payload[44];
  const accel = clampUnit((3 - 0.0000457 * accelRaw) / 3);
  const accelPressed = accel >= PEDAL_PRESS_THRESHOLD;
  if (accelPressed && !prevPressed.accel) {
    dispatch("CONFIRM", "pedal");
  }
  prevPressed.accel = accelPressed;

  // 브레이크 페달 → BACK(source=pedal).
  const brakeRaw = (payload[47] << 8) | payload[46];
  const brake = clampUnit((5 - 0.0000762 * brakeRaw) / 5);
  const brakePressed = brake >= PEDAL_PRESS_THRESHOLD;
  if (brakePressed && !prevPressed.brake) {
    // 항상 켜진 진단(실차 콕핏 devtools 용) — 브레이크 BACK 이 발화되는지 + 이를
    // 받는 활성 구독자(브레이크 페달 허용) 수를 확인. pedalBackSubscribers 가 0 이면
    // 디코드는 정상이나 게이트(예: 제어 중 → enabled=false)로 막힌 것.
    console.log("[WHEEL] BRAKE pressed → dispatch BACK", {
      brake: Number(brake.toFixed(3)),
      pedalBackSubscribers: countEnabledBrakeBackSubscribers(),
      totalSubscribers: subscribers.size,
    });
    dispatch("BACK", "pedal");
  }
  prevPressed.brake = brakePressed;

  // D-pad / 버튼.
  const dpad = payload[4];
  const dpadUp = dpad === 0x00;
  const dpadDown = dpad === 0x04;
  const dpadLeft = dpad === 0x06;
  const dpadRight = dpad === 0x02;
  const cross = dpad === 0x28; // X = CONFIRM
  const circle = dpad === 0x48; // ○ = BACK

  if (dpadUp && !prevPressed.dpadUp) dispatch("UP", "button");
  if (dpadDown && !prevPressed.dpadDown) dispatch("DOWN", "button");
  if (dpadLeft && !prevPressed.dpadLeft) dispatch("LEFT", "button");
  if (dpadRight && !prevPressed.dpadRight) dispatch("RIGHT", "button");
  if (cross && !prevPressed.cross) dispatch("CONFIRM", "button");
  if (circle && !prevPressed.circle) dispatch("BACK", "button");
  prevPressed.dpadUp = dpadUp;
  prevPressed.dpadDown = dpadDown;
  prevPressed.dpadLeft = dpadLeft;
  prevPressed.dpadRight = dpadRight;
  prevPressed.cross = cross;
  prevPressed.circle = circle;

  // Setting Spinner Enter(바이트 53 bit0) = CONFIRM(button).
  const enter = (payload[53] & 1) === 1;
  if (enter && !prevPressed.enter) dispatch("CONFIRM", "button");
  prevPressed.enter = enter;
}

function handleInputReport(event: any): void {
  if (disposed) return;
  decodeAndEmit(event.data as DataView);
}

async function openHidWheel(): Promise<void> {
  try {
    if (!navigator.hid) {
      console.warn("[WheelMenuNav] WebHID 미지원 — 휠 메뉴/페달 내비 비활성");
      return;
    }
    const devices = (await navigator.hid.getDevices?.()) ?? [];
    const device = findAuthorizedLogitechWheel(devices);
    // 항상 켜진 진단(실차 콕핏 devtools 용) — 권한 부여된 휠을 찾았는지/열렸는지 확인.
    // authorizedDeviceCount 가 0 이면 getDevices() 가 비어 있음(권한 미부여/디바이스 미연결).
    console.log("[WHEEL] openHidWheel", {
      authorizedDeviceCount: devices.length,
      foundAuthorizedWheel: Boolean(device),
      alreadyOpened: device?.opened ?? null,
    });
    if (!device || disposed) return;
    // 재연결로 새 장치 객체가 오면 옛 장치에 건 리스너를 먼저 뗀다 — 중복 디코드 방지.
    if (hidDevice && hidDevice !== device) {
      try {
        hidDevice.removeEventListener?.("inputreport", handleInputReport);
      } catch {
        // no-op
      }
    }
    if (hidDevice === device && device.opened) {
      if (!disposed) setConnected(true);
      return;
    }
    hidDevice = device;
    // oninputreport 는 "단일 슬롯" 속성이라, direct-control 의 LogitechG923Wheel
    // 드라이버가 같은 장치에 oninputreport 를 걸면 서로 덮어써서(마지막 설정자만 승리)
    // 허브가 리포트를 못 받아 브레이크 BACK 이 디코드되지 않던 버그가 있었다.
    // addEventListener("inputreport") 는 다중 리스너를 허용하므로 드라이버와 공존한다.
    device.addEventListener?.("inputreport", handleInputReport);
    if (!device.opened) {
      await device.open();
    }
    console.log("[WHEEL] HID wheel opened — 페달/휠 메뉴 내비 활성", {
      opened: device.opened ?? null,
      // 현재 활성(브레이크 BACK 수신) 구독자 수 — 0 이면 게이트(제어 중 등)로 막힌 상태.
      enabledBrakeBackSubscribers: countEnabledBrakeBackSubscribers(),
    });
    if (!disposed) setConnected(true);
  } catch (err) {
    // 휠 열기 실패 — 게임패드/키보드로 대체.
    console.warn("[WheelMenuNav] HID wheel open 실패 — 게임패드/키보드로 대체", {
      reason: err instanceof Error ? err.message : String(err),
    });
  }
}

function readGamepadButton(
  pad: Gamepad,
  index: number,
  key: string,
  event: WheelMenuEvent,
  source: EventSource,
): void {
  const pressed = Boolean(pad.buttons[index]?.pressed);
  if (pressed && !gamepadButtonPrev[key]) {
    dispatch(event, source);
  }
  gamepadButtonPrev[key] = pressed;
}

function pollGamepad(): void {
  const pads = navigator.getGamepads ? navigator.getGamepads() : [];
  const pad = pads.find((gp) => gp && gp.connected && gp.mapping === "standard");

  if (pad) {
    if (!connected) setConnected(true);

    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    if (axisArmed) {
      if (ay <= -GAMEPAD_AXIS_THRESHOLD) {
        dispatch("UP", "wheel");
        axisArmed = false;
      } else if (ay >= GAMEPAD_AXIS_THRESHOLD) {
        dispatch("DOWN", "wheel");
        axisArmed = false;
      } else if (ax <= -GAMEPAD_AXIS_THRESHOLD) {
        dispatch("LEFT", "wheel");
        axisArmed = false;
      } else if (ax >= GAMEPAD_AXIS_THRESHOLD) {
        dispatch("RIGHT", "wheel");
        axisArmed = false;
      }
    } else if (
      Math.abs(ax) <= GAMEPAD_AXIS_DEADZONE &&
      Math.abs(ay) <= GAMEPAD_AXIS_DEADZONE
    ) {
      axisArmed = true;
    }

    readGamepadButton(pad, GP_BTN.A, "gpA", "CONFIRM", "button");
    readGamepadButton(pad, GP_BTN.B, "gpB", "BACK", "button");
    readGamepadButton(pad, GP_BTN.DPAD_UP, "gpUp", "UP", "button");
    readGamepadButton(pad, GP_BTN.DPAD_DOWN, "gpDown", "DOWN", "button");
    readGamepadButton(pad, GP_BTN.DPAD_LEFT, "gpLeft", "LEFT", "button");
    readGamepadButton(pad, GP_BTN.DPAD_RIGHT, "gpRight", "RIGHT", "button");
  } else if (connected && !hidDevice) {
    setConnected(false);
  }

  rafId = requestAnimationFrame(pollGamepad);
}

/**
 * USB 재연결 처리.
 *
 * 허브는 시작할 때 한 번만 휠을 열었다. 휠이 빠졌다 다시 꽂히면 브라우저는 **새 장치
 * 객체**를 주는데 아무도 열지 않아, 화면을 옮기거나 새로고침할 때까지 휠·페달 메뉴
 * 내비가 먹통이었다(운영 PC 실측: 재연결 후 열린 장치 0). 연결/해제를 직접 듣는다.
 * G923 은 HID 장치가 셋이라 조이스틱 컬렉션을 가진 장치에만 반응한다.
 */
function handleHidConnect(event: any): void {
  if (!started || disposed) return;
  if (!findAuthorizedLogitechWheel([event?.device])) return;
  void openHidWheel();
}

function handleHidDisconnect(event: any): void {
  if (!hidDevice || event?.device !== hidDevice) return;
  try {
    hidDevice.removeEventListener?.("inputreport", handleInputReport);
  } catch {
    // no-op
  }
  hidDevice = null;
  setConnected(false);
}

function startHub(): void {
  if (started) return;
  started = true;
  disposed = false;
  navigator.hid?.addEventListener?.("connect", handleHidConnect);
  navigator.hid?.addEventListener?.("disconnect", handleHidDisconnect);
  void openHidWheel();
  rafId = requestAnimationFrame(pollGamepad);
}

function stopHub(): void {
  if (!started) return;
  started = false;
  disposed = true;
  navigator.hid?.removeEventListener?.("connect", handleHidConnect);
  navigator.hid?.removeEventListener?.("disconnect", handleHidDisconnect);
  if (rafId != null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (hidDevice) {
    try {
      // addEventListener 로 건 리스너만 해제 — 드라이버의 oninputreport 는 건드리지 않음.
      hidDevice.removeEventListener?.("inputreport", handleInputReport);
    } catch {
      // no-op
    }
    hidDevice = null;
  }
  connected = false;
  wheelArmed = true;
  axisArmed = true;
}

// 브라우저가 WebHID(게이밍 휠 연결)를 지원하는지.
export function isWheelHidSupported(): boolean {
  return typeof navigator !== "undefined" && Boolean(navigator.hid);
}

// 이미 권한이 부여된(페어링된) 로지텍 휠이 있는지.
export async function hasAuthorizedWheel(): Promise<boolean> {
  try {
    const devices = (await navigator.hid?.getDevices?.()) ?? [];
    return Boolean(findAuthorizedLogitechWheel(devices));
  } catch {
    return false;
  }
}

// 사용자 제스처에서 휠 권한 요청(선택 팝업). 부여되면 허브가 장치를 다시 연다.
export async function requestWheelPermission(): Promise<boolean> {
  try {
    if (!navigator.hid) return false;
    await navigator.hid.requestDevice({ filters: LOGITECH_DEVICE_FILTERS });
    if (started) {
      await openHidWheel();
    }
    return hasAuthorizedWheel();
  } catch {
    return false;
  }
}

export function useWheelMenuNav(options: UseWheelMenuNavOptions): void {
  const {
    enabled = true,
    onEvent,
    onConnectionChange,
    pedalConfirm = true,
    pedalBack = true,
    buttonConfirm = true,
    buttonBack = true,
  } = options;

  const subRef = useRef<Subscriber | null>(null);
  if (!subRef.current) {
    subRef.current = {
      onEventRef: { current: onEvent },
      onConnRef: { current: onConnectionChange },
      enabledRef: { current: enabled },
      pedalConfirmRef: { current: pedalConfirm },
      pedalBackRef: { current: pedalBack },
      buttonConfirmRef: { current: buttonConfirm },
      buttonBackRef: { current: buttonBack },
    };
  }
  // 최신 값으로 갱신(재구독 없이).
  subRef.current.onEventRef.current = onEvent;
  subRef.current.onConnRef.current = onConnectionChange;
  subRef.current.enabledRef.current = enabled;
  subRef.current.pedalConfirmRef.current = pedalConfirm;
  subRef.current.pedalBackRef.current = pedalBack;
  subRef.current.buttonConfirmRef.current = buttonConfirm;
  subRef.current.buttonBackRef.current = buttonBack;

  useEffect(() => {
    const sub = subRef.current!;
    subscribers.add(sub);
    startHub();
    // 이미 연결돼 있으면 신규 구독자에게 즉시 상태 통지.
    if (connected) {
      sub.onConnRef.current?.(true);
    }
    return () => {
      subscribers.delete(sub);
      if (subscribers.size === 0) {
        stopHub();
      }
    };
  }, []);
}
